import React from 'react';

import type { ChatType } from '@modules/types/graphql';

type ChatsManagerFilters = {
    chatId?: string;
    type?: ChatType;
    entityId?: string;
    ownerId?: string;
};

type ChatParams = {
    /** Отключение кнопки чата, которая по умолчанию выводится справа */
    disableChatButton?: boolean;
    /** Сброс фильтров при закрытии */
    resetFiltersAfterClose?: boolean;
};

export type ChatsManagerContextValue = {
    filters: ChatsManagerFilters | null;
    onSetFilters: (payload: ChatsManagerFilters) => void;
    onResetFilters: () => void;
    openChatByFilters: (filters: ChatsManagerFilters, params: ChatParams) => void;
    openDrawer: boolean;
    onOpenDrawer: () => void;
    onCloseDrawer: () => void;
    chatParams: ChatParams | null;
};

// @ts-ignore
const ChatsManagerContext = React.createContext<ChatsManagerContextValue>({});

type ChatsManagerProviderProps = {
    children: React.ReactNode;
};

const ChatsManagerProvider = (props: ChatsManagerProviderProps) => {
    const { children } = props;

    const [params, setParams] = React.useState<ChatParams | null>(null);
    const [openChatsDrawer, setOpenChatsDrawer] = React.useState(false);
    const [filters, setFilters] = React.useState<ChatsManagerFilters | null>(null);

    const handleOpenChatsDrawer = React.useCallback(() => setOpenChatsDrawer(true), []);
    const handleCloseChatsDrawer = React.useCallback(() => {
        setOpenChatsDrawer(false);

        if (params?.resetFiltersAfterClose) {
            setFilters(null);
        }

        setParams(null);
    }, [params?.resetFiltersAfterClose]);

    const handleSetFilters = React.useCallback(
        (payload: ChatsManagerFilters) => setFilters(payload),
        [],
    );

    const handleResetFilters = React.useCallback(() => setFilters(null), []);

    const handleOpenChatByFilters = React.useCallback(
        (filters: ChatsManagerFilters, params: ChatParams) => {
            setFilters(filters);
            setOpenChatsDrawer(true);
            setParams(params);
        },
        [],
    );

    const ctx = React.useMemo(
        () => ({
            filters,
            onSetFilters: handleSetFilters,
            onResetFilters: handleResetFilters,
            openChatByFilters: handleOpenChatByFilters,
            openDrawer: openChatsDrawer,
            onOpenDrawer: handleOpenChatsDrawer,
            onCloseDrawer: handleCloseChatsDrawer,
            chatParams: params,
        }),
        [
            filters,
            handleSetFilters,
            handleResetFilters,
            handleOpenChatByFilters,
            openChatsDrawer,
            handleOpenChatsDrawer,
            handleCloseChatsDrawer,
            params,
        ],
    );

    return <ChatsManagerContext.Provider value={ctx}>{children}</ChatsManagerContext.Provider>;
};

const useChatsManager = () => React.useContext(ChatsManagerContext);

export { ChatsManagerProvider, useChatsManager };
